import React, { useEffect, createElement } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ErrorBoundary from './components/ui/ErrorBoundary';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import Services from './pages/Services';
import MentalCapacity from './pages/Services/MentalCapacity';
import TraumaPTSD from './pages/Services/TraumaPTSD';
import Specialist from './pages/Services/Specialist';
import Privacy from './pages/Policies/Privacy';
import Terms from './pages/Policies/Terms';
import Cancellation from './pages/Policies/Cancellation';
import ForensicRisk from './pages/Services/ForensicRisk';
import Neurodevelopmental from './pages/Services/Neurodevelopmental';
import ClinicalNegligence from './pages/Services/ClinicalNegligence';
import Sitemap from './pages/Sitemap';
import NotFound from './pages/NotFound';
// Google Analytics 4 - inject directly into DOM so Google can detect it
function GoogleAnalytics() {
  useEffect(() => {
    // Only inject once
    if (document.getElementById('gtag-script')) return;
    const gtagScript = document.createElement('script');
    gtagScript.id = 'gtag-script';
    gtagScript.async = true;
    gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-HT5Z5GWWNY';
    document.head.insertBefore(gtagScript, document.head.firstChild);
    const inlineScript = document.createElement('script');
    inlineScript.id = 'gtag-inline';
    inlineScript.textContent = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-HT5Z5GWWNY');
    `;
    document.head.insertBefore(inlineScript, gtagScript.nextSibling);
  }, []);
  return null;
}
// LinkedIn Insight Tag - injected into the DOM so LinkedIn can detect it.
// Partner ID 9721298. Mirrors the official snippet; the <noscript> pixel is
// rendered below so it is present in the markup for non-JS user agents.
const LINKEDIN_PARTNER_ID = '9721298';
function LinkedInInsightTag() {
  useEffect(() => {
    // Only inject once
    if (document.getElementById('linkedin-insight-init')) return;
    const initScript = document.createElement('script');
    initScript.id = 'linkedin-insight-init';
    initScript.type = 'text/javascript';
    initScript.textContent = `
      _linkedin_partner_id = "${LINKEDIN_PARTNER_ID}";
      window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
      window._linkedin_data_partner_ids.push(_linkedin_partner_id);
    `;
    document.body.appendChild(initScript);
    const loaderScript = document.createElement('script');
    loaderScript.id = 'linkedin-insight-loader';
    loaderScript.type = 'text/javascript';
    loaderScript.textContent = `
      (function(l) {
        if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
        window.lintrk.q=[]}
        var s = document.getElementsByTagName("script")[0];
        var b = document.createElement("script");
        b.type = "text/javascript";b.async = true;
        b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
        s.parentNode.insertBefore(b, s);})(window.lintrk);
    `;
    document.body.appendChild(loaderScript);
  }, []);
  return <noscript data-id="element-1" data-mp="f7vxrkyf4okn">
      <img height="1" width="1" style={{
      display: 'none'
    }} alt="" src={`https://px.ads.linkedin.com/collect/?pid=${LINKEDIN_PARTNER_ID}&fmt=gif`} data-id="element-2" data-mp="f8nks7dy9dzb" />
    </noscript>;
}
// ScrollToTop component to ensure page loads at the top
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
// Skip to content link for accessibility
function SkipToContent() {
  return <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-secondary focus:text-white focus:rounded-md focus:shadow-lg" data-id="element-3" data-mp="3noz461pg81x9">
      Skip to main content
    </a>;
}
export function AppRouter() {
  return <BrowserRouter data-id="element-4" data-mp="1hs3s5s9c1vct">
      {/* Site-wide head only. Per-page title, description, canonical, Open Graph,
          Twitter card and page schema are set by components/ui/SEO.tsx. */}
      <Helmet data-id="element-5" data-mp="18ryj7dohqowj">
        <html lang="en-GB" data-id="element-6" data-mp="18vcksjpbyu2x" />
        <link rel="preconnect" href="https://fonts.googleapis.com" data-id="element-7" data-mp="1ds2zp3pefjmr" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" data-id="element-8" data-mp="1dq9wjydzvhji" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Merriweather:wght@400;700&display=swap" rel="stylesheet" data-id="element-9" data-mp="13u6pqf876uq8" />
        <link rel="icon" href="https://uploadthingy.s3.us-west-1.amazonaws.com/hDXV1rDXM1m2ARqkQ9Hg8F/Medico_Favicon.png" type="image/png" data-id="element-10" data-mp="2t7ab30tonbv5" />
        <link rel="preconnect" href="https://uploadthingy.s3.us-west-1.amazonaws.com" crossOrigin="" data-id="element-11" data-mp="ml491x6l132q" />
        {/* Above-the-fold logo, preloaded to protect largest contentful paint */}
        <link rel="preload" as="image" href="https://uploadthingy.s3.us-west-1.amazonaws.com/hDXV1rDXM1m2ARqkQ9Hg8F/Medico_Favicon.png" data-id="element-12" data-mp="wch38vfus5bx" />
        <script type="application/ld+json" data-id="element-13" data-mp="159izojczmf30">
          {`
            {
              "@context": "https://schema.org",
              "@type": "ProfessionalService",
              "name": "Medicolegal Psychiatry",
              "url": "https://medicolegal-psychiatry.co.uk",
              "email": "enquiries@medicolegal-psychiatry.co.uk",
              "areaServed": ["England", "Wales"],
              "serviceType": "Psychiatric expert witness reports",
              "founder": {
                "@type": "Person",
                "name": "Dr Imtiaz Syed",
                "jobTitle": "Independent Psychiatric Expert Witness"
              }
            }
          `}
        </script>
      </Helmet>
      <GoogleAnalytics data-id="element-14" data-mp="1ky08z5hxdq3w" />
      <ErrorBoundary data-id="element-15" data-mp="1ozbuaytrnp6a">
        <SkipToContent data-id="element-16" data-mp="1oytwvyvdb4ws" />
        <div className="flex flex-col min-h-screen" data-id="element-17" data-mp="1oyka0k5mddvb">
          <Header data-id="element-18" data-mp="1tyevu9ajfd9i" />
          <ScrollToTop data-id="element-19" data-mp="1tyoqwbafwam2" />
          <main id="main-content" className="flex-grow" data-id="element-20" data-mp="1tz7wqtmtf8jq">
            <Routes data-id="element-21" data-mp="1yxrj2v5bmmcm">
              <Route path="/" element={<Home data-id="element-23" data-mp="1yx00ckwtoj3r" />} data-id="element-22" data-mp="1yyb10buz1aqn" />
              <Route path="/about" element={<About data-id="element-25" data-mp="23w2uvbf6f6xh" />} data-id="element-24" data-mp="1yvoxcnb7bfdp" />
              <Route path="/services" element={<Services data-id="element-27" data-mp="23uhzjjl1fjyk" />} data-id="element-26" data-mp="23vksry5u1yhn" />
              <Route path="/services/mental-capacity" element={<MentalCapacity data-id="element-29" data-mp="28poy6czg9ygm" />} data-id="element-28" data-mp="28nkx02sxrdk1" />
              <Route path="/services/trauma-ptsd" element={<TraumaPTSD data-id="element-31" data-mp="2cr0tmz509dwq" />} data-id="element-30" data-mp="2cnv2lectg1yd" />
              <Route path="/services/specialist" element={<Specialist data-id="element-33" data-mp="2hpkf6yfosnok" />} data-id="element-32" data-mp="2hoj1snb77p3o" />
              <Route path="/services/forensic-risk" element={<ForensicRisk data-id="element-35" data-mp="2mndm5totdlpw" />} data-id="element-34" data-mp="2hm6dlseukii6" />
              <Route path="/services/neurodevelopmental" element={<Neurodevelopmental data-id="element-37" data-mp="2rs7k40bisyek" />} data-id="element-36" data-mp="2wrk84oe4obiq" />
              <Route path="/services/clinical-negligence" element={<ClinicalNegligence data-id="element-39" data-mp="2mtmjhzscwcpl" />} data-id="element-38" data-mp="2rqd0cpbzjcc2" />
              <Route path="/sitemap" element={<Sitemap data-id="element-41" data-mp="2is17shrk64gz" />} data-id="element-40" data-mp="2ir9ghj4zuswi" />
              <Route path="/faq" element={<FAQ data-id="element-43" data-mp="2itm6mjkwo9kn" />} data-id="element-42" data-mp="2isuc7c31ohvy" />
              <Route path="/contact" element={<Contact data-id="element-45" data-mp="3fg7b91cquqto" />} data-id="element-44" data-mp="3fi1wyhvuamyt" />
              <Route path="/policies/privacy" element={<Privacy data-id="element-47" data-mp="3ahxfopn3xo7j" />} data-id="element-46" data-mp="3fjmpk1ylfvu1" />
              <Route path="/policies/terms" element={<Terms data-id="element-49" data-mp="3akrzqg27l70f" />} data-id="element-48" data-mp="3ajqkdze5tycw" />
              <Route path="/policies/cancellation" element={<Cancellation data-id="element-51" data-mp="35q5u6tit68o9" />} data-id="element-50" data-mp="35r78d6v4fba7" />
              <Route path="*" element={<NotFound data-id="element-53" data-mp="31qn9nyuwfzly" />} data-id="element-52" data-mp="31q3uur0jprc5" />
            </Routes>
          </main>
          <Footer data-id="element-54" data-mp="31oavlws9i9e6" />
          {/* Insight Tag sits at the end of the body, per LinkedIn's guidance */}
          <LinkedInInsightTag data-id="element-55" data-mp="24glt19hbb9v" />
        </div>
      </ErrorBoundary>
    </BrowserRouter>;
}